import type { AuthUser } from '@/types/api';
import { isStaff, landingPathFor } from '@/lib/landing';

/**
 * Top-level sections inside the signed-in application.
 *
 * Kept as a list rather than derived from the router because the router's route objects are not
 * reachable from here without restructuring App.tsx. That means this needs updating when a section
 * is added — the cost of getting it wrong is a redirect that falls back to the dashboard, which is
 * the safe direction to fail in.
 */
const APP_SECTIONS = [
  'dashboard',
  'system',
  'automation',
  'servers',
  'billing',
  'monitoring',
  'backups',
  'support',
  'settings',
  'admin',
];

/**
 * Where to send someone after they sign in.
 *
 * ## Why `from` cannot simply be trusted
 *
 * `ProtectedRoute` records the path that bounced an unauthenticated visitor to the login screen, so
 * signing in returns them to what they were reaching for. That is the right behaviour, and it was
 * being applied without checking whether the destination is somewhere this particular account can
 * go.
 *
 * The catch-all `NotFoundPage` lives *inside* the application shell, so an unreachable path does
 * not look like a broken link — it renders a 404 with the sidebar still around it, exactly as if
 * the dashboard itself had failed. Clicking "Dashboard" then "fixes" it, which makes the real cause
 * almost impossible to guess from the symptom.
 *
 * Three ways `from` goes bad:
 *
 *   - it points at `/admin` for an account that is not staff;
 *   - it points at a section that no longer exists, from an old tab or a stale bookmark;
 *   - it points at a marketing page or an auth page, which would bounce straight back.
 *
 * In every case the honest answer is the role's own landing page rather than a 404.
 */
export function destinationAfterLogin(from: string | null, user: AuthUser | null): string {
  const fallback = landingPathFor(user);

  if (from === null || !from.startsWith('/')) {
    return fallback;
  }

  // Protocol-relative URLs (`//evil.example.com`) start with a slash and are not local paths.
  if (from.startsWith('//')) {
    return fallback;
  }

  const section = from.split('?')[0].split('#')[0].split('/')[1] ?? '';

  if (!APP_SECTIONS.includes(section)) {
    return fallback;
  }

  if (section === 'admin' && !isStaff(user?.role)) {
    return fallback;
  }

  return from;
}
